import * as React from "react";
import { TextInputEditor, type ParseResult } from "../internal/text-input-editor";
import {
  CURRENCY_DEFAULT_CODE,
  fromCurrencyInputString,
  normalizeCurrencyInput,
  toCurrencyInputString,
  type CurrencyValue,
} from "./currency";

export function CurrencyEditor({
  value,
  locale,
  onCommit,
  onCancel,
}: {
  value: CurrencyValue;
  locale?: string;
  onCommit: (next: CurrencyValue) => void;
  onCancel: () => void;
}) {
  const normalized = React.useMemo(() => normalizeCurrencyInput(value), [value]);
  // Capture currency_code at mount. Subsequent edits must never drop or
  // substitute the code.
  const codeRef = React.useRef<string>(normalized?.currency_code ?? CURRENCY_DEFAULT_CODE);

  // Seed the editor's initial input once at mount. Subsequent prop changes
  // do not reset the draft — a lazy `useState` initializer matches that
  // semantics without an exhaustive-deps escape hatch.
  const [initial] = React.useState<string>(() => {
    const seed = normalizeCurrencyInput(value);
    if (seed == null) return "";
    return toCurrencyInputString(seed.amount, codeRef.current, locale);
  });

  const parse = React.useCallback(
    (draft: string): ParseResult<CurrencyValue> => {
      const trimmed = draft.trim();
      if (trimmed === "") return { ok: true, value: null };
      const parsed = fromCurrencyInputString(trimmed, codeRef.current, locale);
      if (!parsed.ok) return { ok: false };
      return {
        ok: true,
        value: { amount: parsed.amount, currency_code: codeRef.current },
      };
    },
    [locale],
  );

  return (
    <TextInputEditor<CurrencyValue>
      initial={initial}
      parse={parse}
      onCommit={onCommit}
      onCancel={onCancel}
      inputType="text"
      inputMode="decimal"
    />
  );
}
